/**
 * Utilidades de formateo para RAE Project Management
 */
import { Priority, SubElement, TaskStatus, TaskStatusProgress } from '../types';

// ==================== Avatar Color ====================

const AVATAR_COLORS = [
  '#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444',
  '#06b6d4', '#a78bfa', '#f472b6', '#ec4899', '#14b8a6',
];

/** Deterministic avatar color from user ID — same ID always returns the same color */
export function getAvatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ==================== Formatting ====================

export function formatHours(hours: number): string {
  if (hours === 0) return '0h 00m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `0h ${String(m).padStart(2, '0')}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function formatPercentage(value: number): string {
  return `${Math.round(value)}%`;
}

// ==================== Task Progress ====================

/**
 * Avance de una tarea (0-100) según su estado.
 *
 * PAUSED e ISSUES no tienen avance propio: conservan el que la tarea traía
 * antes de interrumpirse, recuperado del historial. Sin esto, una tarea en
 * revisión (80%) que reporta un problema caería al 50%.
 */
export function getTaskProgress(task: Pick<SubElement, 'status' | 'statusHistory'>): number {
  if (task.status !== 'PAUSED' && task.status !== 'ISSUES') {
    return TaskStatusProgress[task.status];
  }

  const history = task.statusHistory || [];
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry.field !== 'status') continue;
    const previous = entry.oldValue as TaskStatus;
    if (previous && previous !== 'PAUSED' && previous !== 'ISSUES' && previous in TaskStatusProgress) {
      return TaskStatusProgress[previous];
    }
  }
  return TaskStatusProgress[task.status];
}

/** Promedio de avance de un conjunto de tareas. */
export function averageProgress(tasks: Pick<SubElement, 'status' | 'statusHistory'>[]): number {
  if (tasks.length === 0) return 0;
  const sum = tasks.reduce((acc, t) => acc + getTaskProgress(t), 0);
  return Math.round(sum / tasks.length);
}

export function generateTempId(prefix: string = 'temp'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ==================== Date Helpers ====================

export function formatDateShort(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function getWeekKey(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  const monday = new Date(d);
  monday.setDate(diff);
  return monday.toISOString().split('T')[0];
}

export function getWeekRange(weekKey: string): { start: Date; end: Date; label: string } {
  const start = new Date(weekKey + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const label = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  return { start, end, label };
}

export function isOverdue(endDate: string, status: string): boolean {
  if (!endDate || status === 'FINISHED') return false;
  return new Date() > new Date(endDate + 'T23:59:59');
}

export function validateDateRange(startDate: string, endDate: string): string | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'Invalid date format.';
  }
  if (start > end) {
    return 'Start date cannot be after end date.';
  }
  return null;
}

export function validateEstimatedHours(value: string): string | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'Estimated hours must be a valid number.';
  if (parsed < 0) return 'Estimated hours cannot be negative.';
  if (parsed > 10000) return 'Estimated hours is too high.';
  return null;
}

// ==================== Priority Escalation ====================

const PRIORITY_RANK: Record<Priority, number> = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };

interface EscalationInput {
  priority: Priority;
  endDate: string;
  status: string;
}

function daysUntilDeadline(endDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endDate + 'T00:00:00');
  end.setHours(0, 0, 0, 0);
  return Math.floor((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Returns the effective display priority, auto-escalating based on deadline proximity.
 * Never downgrades — only escalates.
 * - 0 days or overdue → CRITICAL minimum
 * - 1 day remaining  → HIGH minimum
 * - FINISHED tasks   → original priority (no escalation)
 */
export function getEffectivePriority(task: EscalationInput): Priority {
  if (task.status === 'FINISHED' || !task.endDate) return task.priority;
  const days = daysUntilDeadline(task.endDate);
  let escalated: Priority = task.priority;
  if (days <= 0) escalated = 'CRITICAL';
  else if (days === 1) escalated = 'HIGH';
  return PRIORITY_RANK[escalated] > PRIORITY_RANK[task.priority] ? escalated : task.priority;
}

/**
 * Returns true if the task's priority was auto-escalated due to deadline proximity.
 * Used to show a visual indicator in the UI.
 */
export function isAutoEscalated(task: EscalationInput): boolean {
  if (task.status === 'FINISHED' || !task.endDate) return false;
  const days = daysUntilDeadline(task.endDate);
  if (days <= 0) return PRIORITY_RANK['CRITICAL'] > PRIORITY_RANK[task.priority];
  if (days === 1) return PRIORITY_RANK['HIGH'] > PRIORITY_RANK[task.priority];
  return false;
}
